import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { ColumnDef, DataTable } from '../Table/DataTable';
import { Header } from '../Header/Header';
import { FreshnessBadge } from './FreshnessBadge';
import {
  MapStatusChip,
  MappingStatusFilter,
  MappingTypeTabs,
  mappingActionLinkSx,
  mappingCellText,
  mappingEmptyStateSx,
  mappingToolbarSx,
} from './MappingShared';
import vendingApiClient from '../../Services/VendingApi';
import { formatUTC } from '../../Services/FormatUtc';
import { PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';
import {
  AimsUserSearchResponse,
  AimsUserSummary,
  EmployeeMappingListResponse,
  EmployeeMappingMachine,
  EmployeeMappingRow,
  EmployeeMappingSuggestion,
} from '../../Types/vending';

type MapStatusFilter = 'unmapped' | 'mapped' | 'conflict' | 'all';

const SEARCH_DEBOUNCE_MS = 350;

const aimsUserLabel = (user: AimsUserSummary | null) => {
  if (!user) return '—';
  const name = user.display_name
    || [user.first_name, user.last_name].filter(Boolean).join(' ')
    || user.username;
  const detail = user.username && user.username !== name ? user.username : null;
  return detail ? `${name} (${detail})` : (name || `User ${user.uid}`);
};

export const VendingEmployeeMappingPage = () => {
  const [rows, setRows] = useState<EmployeeMappingRow[]>([]);
  const [stats, setStats] = useState({ unmapped: 0, mapped: 0, conflict: 0 });
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<MapStatusFilter>('unmapped');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<EmployeeMappingRow | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<AimsUserSummary[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUid, setSelectedUid] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadMappings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '200', map_status: statusFilter });
      if (debouncedSearch) params.set('q', debouncedSearch);
      const response = await vendingApiClient(`/mappings/employees?${params.toString()}`);
      const json: EmployeeMappingListResponse = await response.json();
      setRows(json.data);
      setAsOf(json.meta?.as_of ?? null);
      setStats({
        unmapped: json.meta?.stats?.unmapped ?? 0,
        mapped: json.meta?.stats?.mapped ?? 0,
        conflict: json.meta?.stats?.conflict ?? 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load employee mappings');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  useEffect(() => {
    if (!dialogOpen || userSearch.trim().length < 2) {
      setUserResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const params = new URLSearchParams({ q: userSearch.trim() });
        const response = await vendingApiClient(`/mappings/employees/user-search?${params.toString()}`);
        const json: AimsUserSearchResponse = await response.json();
        setUserResults(json.data);
      } catch {
        setUserResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [dialogOpen, userSearch]);

  const openMapDialog = (row: EmployeeMappingRow, suggestion?: EmployeeMappingSuggestion) => {
    setSelected(row);
    setSelectedUid(suggestion?.uid ?? row.uid ?? null);
    setUserSearch(
      suggestion?.username
      || suggestion?.display_name
      || row.employee_number
      || row.name
      || '',
    );
    setSuccess(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelected(null);
    setSelectedUid(null);
    setUserSearch('');
    setUserResults([]);
  };

  const submitMapping = async (body: Record<string, unknown>) => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await vendingApiClient(`/mappings/employees/${selected.id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      closeDialog();
      setSuccess(body.action === 'unmap' ? 'Employee mapping cleared.' : 'Employee mapped to AIMS user.');
      await loadMappings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!selectedUid) return;
    submitMapping({ uid: selectedUid });
  };

  const handleQuickAction = useCallback(async (row: EmployeeMappingRow, body: Record<string, unknown>) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await vendingApiClient(`/mappings/employees/${row.id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      setSuccess(body.action === 'unmap' ? 'Employee mapping cleared.' : 'Employee mapped.');
      await loadMappings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update mapping');
    } finally {
      setSaving(false);
    }
  }, [loadMappings]);

  const columns: ColumnDef<EmployeeMappingRow>[] = useMemo(() => [
    {
      field: 'name',
      headerName: 'Vendor employee',
      width: 200,
      minWidth: 160,
      searchable: true,
      valueGetter: (_v, row) => row.name || '—',
      renderCell: (params) => (
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <Typography sx={{ ...mappingCellText, fontWeight: 600 }}>
            {params.row.name || '—'}
          </Typography>
          {params.row.employee_number && (
            <Typography variant="caption" sx={{ color: COLORS.lightGray, display: 'block' }}>
              #{params.row.employee_number}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'department',
      headerName: 'Department',
      width: 140,
      minWidth: 110,
      valueGetter: (_v, row) => row.department || '—',
      renderCell: (params) => (
        <Typography sx={mappingCellText}>{params.row.department || '—'}</Typography>
      ),
    },
    {
      field: 'machines',
      headerName: 'Machines',
      width: 180,
      minWidth: 140,
      sortable: false,
      valueGetter: (_v, row) => {
        const machines = Array.isArray(row.machines) ? row.machines : [];
        if (!machines.length) return '—';
        return machines
          .map((m: EmployeeMappingMachine) => m.machine_name || `VM-${m.provider_machine_id}`)
          .join(', ');
      },
      renderCell: (params) => {
        const machines = Array.isArray(params.row.machines) ? params.row.machines : [];
        if (!machines.length) {
          return <Typography sx={mappingCellText}>—</Typography>;
        }
        const full = machines
          .map((m: EmployeeMappingMachine) => m.machine_name || `VM-${m.provider_machine_id}`)
          .join(', ');
        const label = machines.length === 1
          ? (machines[0].machine_name || `VM-${machines[0].provider_machine_id}`)
          : `${machines.length} machines`;
        return (
          <Typography sx={mappingCellText} title={full}>
            {label}
          </Typography>
        );
      },
    },
    {
      field: 'map_status',
      headerName: 'Status',
      width: 110,
      minWidth: 100,
      valueGetter: (_v, row) => row.map_status,
      renderCell: (params) => (
        <MapStatusChip status={params.row.map_status ?? params.value} />
      ),
    },
    {
      field: 'aims_user',
      headerName: 'AIMS user',
      width: 220,
      minWidth: 160,
      valueGetter: (_v, row) => aimsUserLabel(row.aims_user),
      renderCell: (params) => (
        <Typography sx={mappingCellText} title={aimsUserLabel(params.row.aims_user)}>
          {aimsUserLabel(params.row.aims_user)}
        </Typography>
      ),
    },
    {
      field: 'id',
      headerName: '',
      width: 120,
      minWidth: 100,
      sortable: false,
      filterable: false,
      valueGetter: () => '',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
          {params.row.map_status !== 'mapped' && (
            <Typography
              component="button"
              disabled={saving}
              onClick={(e) => {
                e.stopPropagation();
                openMapDialog(params.row, params.row.suggestions?.[0]);
              }}
              sx={mappingActionLinkSx}
            >
              Map
            </Typography>
          )}
          {(params.row.map_status === 'mapped' || params.row.map_status === 'conflict') && (
            <Typography
              component="button"
              disabled={saving}
              onClick={(e) => {
                e.stopPropagation();
                handleQuickAction(params.row, { action: 'unmap' });
              }}
              sx={mappingActionLinkSx}
            >
              Unmap
            </Typography>
          )}
        </Box>
      ),
    },
  ], [handleQuickAction, saving]);

  const subtitle = useMemo(() => {
    const parts = [
      `${stats.unmapped} unmapped`,
      `${stats.mapped} mapped`,
    ];
    if (stats.conflict) parts.push(`${stats.conflict} conflict`);
    if (asOf) parts.push(`As of ${formatUTC(asOf)} UTC`);
    return parts.join(' · ');
  }, [stats, asOf]);

  return (
    <PageContainer>
      <Header title="Mappings" subtitle={subtitle} count={rows.length}>
        <FreshnessBadge asOf={asOf} />
        <Button
          variant="outlined"
          onClick={loadMappings}
          disabled={loading}
          sx={{
            borderColor: COLORS.teal,
            color: COLORS.teal,
            textTransform: 'none',
            '&:hover': { borderColor: COLORS.darkTeal, backgroundColor: `${COLORS.teal}0a` },
          }}
        >
          Refresh
        </Button>
      </Header>

      <MappingTypeTabs active="employees" refreshKey={success} />

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={mappingToolbarSx}>
          <MappingStatusFilter<MapStatusFilter>
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'unmapped', label: `Unmapped (${stats.unmapped})` },
              { value: 'mapped', label: `Mapped (${stats.mapped})` },
              ...(stats.conflict > 0 ? [{ value: 'conflict' as const, label: `Conflict (${stats.conflict})` }] : []),
              { value: 'all', label: 'All' },
            ]}
          />

          <TextField
            size="small"
            label="Search employees"
            placeholder="Name, employee #, department…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 280, maxWidth: 420 }}
          />

          {success && (
            <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6, flexShrink: 0 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!loading && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {rows.length === 0 ? (
              <Box sx={mappingEmptyStateSx}>
                <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.darkGray, mb: 1 }}>
                  No employees to map
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
                  Run an employees sync from Integration Health first, then map vendor employees to AIMS users here.
                </Typography>
              </Box>
            ) : (
              <DataTable
                columns={columns}
                data={rows.map((row) => ({ ...row, id: row.id }))}
                pageKey="vending-employee-mapping-v4"
                idField="id"
                rowHeight={56}
              />
            )}
          </Box>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Map vendor employee to AIMS user</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.lightGray }}>
                Vendor employee
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color: COLORS.darkGray }}>
                {selected.name || '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.lightGray }}>
                {[
                  selected.employee_number ? `#${selected.employee_number}` : null,
                  selected.department,
                ].filter(Boolean).join(' · ') || 'No extra details'}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            size="small"
            label="Search AIMS users"
            placeholder="Username, name, email, RFID…"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            sx={{ mb: 2 }}
          />

          {userSearchLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: COLORS.teal }} />
            </Box>
          )}

          {!userSearchLoading && userResults.length > 0 && (
            <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
              {userResults.map((user) => (
                <Box
                  key={user.uid}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 1,
                    borderBottom: `1px solid ${COLORS.lightGray}30`,
                    bgcolor: selectedUid === user.uid ? `${COLORS.teal}10` : 'transparent',
                    borderRadius: 1,
                    px: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{aimsUserLabel(user)}</Typography>
                    <Typography variant="caption" sx={{ color: COLORS.lightGray }}>
                      {[user.email, user.rfid ? `RFID ${user.rfid}` : null].filter(Boolean).join(' · ') || `UID ${user.uid}`}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant={selectedUid === user.uid ? 'contained' : 'text'}
                    onClick={() => setSelectedUid(user.uid)}
                    sx={{
                      textTransform: 'none',
                      ...(selectedUid === user.uid
                        ? { bgcolor: COLORS.teal, boxShadow: 'none', '&:hover': { bgcolor: COLORS.darkTeal } }
                        : { color: COLORS.teal }),
                    }}
                  >
                    Select
                  </Button>
                </Box>
              ))}
            </Box>
          )}

          {!userSearchLoading && userSearch.trim().length >= 2 && userResults.length === 0 && (
            <Typography variant="body2" sx={{ color: COLORS.lightGray, py: 1 }}>
              No AIMS users match that search.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !selectedUid}
            sx={{
              backgroundColor: COLORS.teal,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { backgroundColor: COLORS.darkTeal, boxShadow: 'none' },
            }}
          >
            {saving ? 'Saving…' : 'Save mapping'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default VendingEmployeeMappingPage;
